import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Home, Archive, Users, CalendarDays, Droplets, BookOpen, UserCircle, Settings, Activity, LogOut, Bell, HeartHandshake, MessageCircle } from "lucide-react";
import { usePortalAuth, ProtectedRoute } from "@/context/PortalAuth";
import { portalApi } from "@/lib/portalApi";
import Celebration from "./Celebration";
import Avatar from "./Avatar";
import OnboardingBlessing from "./OnboardingBlessing";

// Six rooms + Library. Everything else flows through the guided journey.
const links = [
  { to: "/portal", label: "Home", icon: Home, end: true, testid: "portal-nav-dashboard" },
  { to: "/portal/vessel", label: "My Vessel", icon: Archive, testid: "portal-nav-vessel" },
  { to: "/portal/community", label: "Community", icon: Users, testid: "portal-nav-community" },
  { to: "/portal/duo", label: "Witnesses", icon: HeartHandshake, testid: "portal-nav-witnesses" },
  { to: "/portal/gatherings", label: "Gatherings", icon: CalendarDays, testid: "portal-nav-calls" },
  { to: "/portal/garden", label: "Living Water", icon: Droplets, testid: "portal-nav-garden" },
  { to: "/portal/library", label: "Library", icon: BookOpen, testid: "portal-nav-library" },
  { to: "/portal/me", label: "Profile", icon: UserCircle, testid: "portal-nav-profile-room" },
];

const pill = (isActive) =>
  `flex items-center gap-2 px-3.5 py-2 rounded-full text-sm whitespace-nowrap transition-colors duration-300 ${
    isActive ? "bg-[#A9724E] text-[#F4EFE6]" : "text-[#D9CFC2] hover:bg-white/10"
  }`;

function PortalLayout() {
  const { user, logout } = usePortalAuth();
  const navigate = useNavigate();
  const doLogout = () => { logout(); navigate("/portal/login"); };

  const [notifs, setNotifs] = useState({ unread: 0, items: [] });
  const [home, setHome] = useState(null);
  const [openBell, setOpenBell] = useState(false);
  const [openMore, setOpenMore] = useState(false);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const bellRef = useRef(null);
  const moreRef = useRef(null);

  const loadNotifs = () => portalApi.get("/portal/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  const loadUnreadMsgs = () => portalApi.get("/portal/messages/unread").then((r) => setUnreadMsgs(r.data.unread)).catch(() => {});
  useEffect(() => { loadNotifs(); loadUnreadMsgs(); const id = setInterval(() => { loadNotifs(); loadUnreadMsgs(); }, 30000); return () => clearInterval(id); }, []);
  useEffect(() => { portalApi.get("/portal/home").then((r) => setHome(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const onClick = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setOpenBell(false);
      if (moreRef.current && !moreRef.current.contains(e.target)) setOpenMore(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggleBell = async () => {
    const next = !openBell;
    setOpenBell(next);
    if (next && notifs.unread > 0) {
      await portalApi.post("/portal/notifications/read").catch(() => {});
      setNotifs((s) => ({ ...s, unread: 0, items: s.items.map((i) => ({ ...i, read: true })) }));
    }
  };

  // Members must complete their blessing card before entering the portal.
  if (user && user.role !== "admin" && user.onboarded === false) {
    return <OnboardingBlessing />;
  }

  return (
    <div className="min-h-screen bg-[#F4EFE6] text-[#2A2825]">
      <header className="sticky top-0 z-40 bg-[#2A2825] text-[#F4EFE6]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <NavLink to="/portal" end className="font-serif-display text-xl tracking-wide">
            Outpoured <span className="text-[#E8B486] italic text-base">{home ? (home.is_admin ? "admin" : home.home_name) : "portal"}</span>
          </NavLink>
          <div className="flex items-center gap-4">
            <NavLink to="/portal/messages" data-testid="portal-messages-icon" className="relative flex items-center text-[#D9CFC2] hover:text-[#F4EFE6] transition-colors">
              <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
              {unreadMsgs > 0 && <span data-testid="portal-messages-badge" className="absolute -top-1.5 -right-1.5 bg-[#A9724E] text-[#F4EFE6] text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{unreadMsgs}</span>}
            </NavLink>
            <div className="relative" ref={bellRef}>
              <button onClick={toggleBell} data-testid="portal-bell" className="relative flex items-center text-[#D9CFC2] hover:text-[#F4EFE6] transition-colors">
                <Bell className="w-5 h-5" strokeWidth={1.5} />
                {notifs.unread > 0 && <span data-testid="portal-bell-badge" className="absolute -top-1.5 -right-1.5 bg-[#A9724E] text-[#F4EFE6] text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{notifs.unread}</span>}
              </button>
              {openBell && (
                <div data-testid="portal-bell-dropdown" className="absolute right-0 mt-3 w-80 bg-[#F4EFE6] text-[#2A2825] rounded-xl shadow-2xl border border-[#E0D8C9] overflow-hidden z-50">
                  <p className="px-4 py-3 border-b border-[#E0D8C9] tracking-overline text-[10px] uppercase text-[#8a7a6a]">Notifications</p>
                  <div className="max-h-80 overflow-y-auto">
                    {notifs.items.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-[#8a7a6a] font-body text-center">Nothing yet.</p>
                    ) : notifs.items.map((n) => (
                      <div key={n.id} className="px-4 py-3 border-b border-[#E0D8C9]/60 last:border-0">
                        <p className="text-xs text-[#A9724E] font-body mb-0.5">{n.from_name}</p>
                        <p className="text-sm text-[#2A2825] font-body leading-snug whitespace-pre-wrap">{n.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/portal/me" data-testid="portal-nav-profile" className="flex items-center gap-2 text-sm text-[#D9CFC2] hover:text-[#F4EFE6] transition-colors">
              <Avatar name={user?.name} avatar={user?.avatar} size={28} className="ring-1 ring-white/20" testid="portal-nav-avatar" /><span className="hidden sm:inline">{(user?.name || "You").split(" ")[0]}</span>
            </NavLink>
            <button onClick={doLogout} data-testid="portal-logout" className="flex items-center gap-2 text-sm text-[#D9CFC2] hover:text-[#F4EFE6] transition-colors">
              <LogOut className="w-4 h-4" strokeWidth={1.5} /><span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto flex items-center gap-1 overflow-x-auto px-4 md:px-8 pb-3 no-scrollbar">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.testid} className={({ isActive }) => pill(isActive)}>
              <l.icon className="w-4 h-4" strokeWidth={1.5} />{l.label}
            </NavLink>
          ))}
          <div className="relative" ref={moreRef}>
            {user?.role === "admin" && (
              <NavLink to="/portal/pulse" data-testid="portal-nav-pulse" className={({ isActive }) => pill(isActive)}>
                <Activity className="w-4 h-4" strokeWidth={1.5} />Pulse
              </NavLink>
            )}
          </div>
          {user?.role === "admin" && (
            <NavLink to="/portal/manage" data-testid="portal-nav-manage" className={({ isActive }) => pill(isActive)}>
              <Settings className="w-4 h-4" strokeWidth={1.5} />Manage
            </NavLink>
          )}
        </nav>
      </header>
      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <Outlet />
      </main>
      <Celebration />
    </div>
  );
}

export default function PortalShell() {
  return (
    <ProtectedRoute>
      <PortalLayout />
    </ProtectedRoute>
  );
}
